import { useTranslation } from 'react-i18next';
/**
 * SystemSupport — tenant-facing /system/support page.
 *
 * List of submitted support cases + detail dialog with reply thread.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Paper, Select,
  FormControl, InputLabel, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { RefreshOutlined, SupportAgentOutlined, CloseOutlined } from '@mui/icons-material';

import { supportApi } from '../../../api';
import type { SupportCase, SupportCaseReply } from '../../../api/support';
import { openSupportCase } from '../../../components/support/SupportCaseDialog';
import {
  supportCategoryLabel,
  supportPriorityLabel,
  supportStatusLabel,
} from '../../../components/support/supportDisplay';


function statusColor(s: string): 'warning' | 'info' | 'success' | 'default' {
  if (s === 'open') return 'warning';
  if (s === 'in_progress') return 'info';
  if (s === 'resolved') return 'success';
  return 'default';
}

function priorityColor(p: string): 'error' | 'warning' | 'default' | 'info' {
  if (p === 'urgent') return 'error';
  if (p === 'high') return 'warning';
  if (p === 'low') return 'default';
  return 'info';
}


const SystemSupport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: /system/support?case_id=<id> opens the case
  // detail dialog. Used by the notification bell click-through (P2.7).
  useEffect(() => {
    const fromUrl = searchParams.get('case_id');
    if (fromUrl && fromUrl !== selectedId) {
      setSelectedId(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: 1, page_size: 50 };
      if (statusFilter) params.status = statusFilter;
      const res = await supportApi.list(params);
      setCases(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setToast(e?.response?.data?.detail || t('system:support.loadCasesFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('system:support.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('system:support.subtitleDetail')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={load} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained" size="small"
            onClick={() => openSupportCase()}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('system:support.newCase')}
          </Button>
        </Stack>
      </Box>

      {/* Filter */}
      <Paper elevation={0} sx={{ width: '100%', mb: 2, p: 1.5, borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('system:support.statusLabel')}</InputLabel>
            <Select label={t('system:support.statusLabel')} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">{t('system:support.allStatuses')}</MenuItem>
              <MenuItem value="open">{t('system:support.statusOpen')}</MenuItem>
              <MenuItem value="in_progress">{t('system:support.statusInProgress')}</MenuItem>
              <MenuItem value="resolved">{t('system:support.statusResolved')}</MenuItem>
              <MenuItem value="closed">{t('system:support.statusClosed')}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {t('system:support.caseCount', { count: total })}
          </Typography>
        </Stack>
      </Paper>

      {/* List */}
      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : cases.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 6, textAlign: 'center', borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)' }}>
          <SupportAgentOutlined sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t('system:support.noCases')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('system:support.noCasesHint')}
          </Typography>
          <Button
            variant="contained" size="small"
            onClick={() => openSupportCase()}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('system:support.newCase')}
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          {cases.map((c) => (
            <Paper
              key={c.id} elevation={0}
              onClick={() => setSelectedId(c.id)}
              sx={{
                p: 2, borderRadius: 2, cursor: 'pointer',
                border: '1px solid rgba(0,0,0,0.12)',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={1.5}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                      {c.subject}
                    </Typography>
                    <Chip label={supportStatusLabel(t, c.status)} size="small" color={statusColor(c.status)}
                      sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                    <Chip label={supportPriorityLabel(t, c.priority)} size="small" color={priorityColor(c.priority)} variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {supportCategoryLabel(t, c.category)} · {c.created_at ? new Date(c.created_at).toLocaleString(i18n.language) : ''}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <CaseDetailDialog
        caseId={selectedId}
        onClose={() => {
          setSelectedId(null);
          if (searchParams.get('case_id')) {
            const next = new URLSearchParams(searchParams);
            next.delete('case_id');
            setSearchParams(next, { replace: true });
          }
        }}
        onChanged={() => { load(); }}
        onError={(msg) => setToast(msg)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Detail dialog with reply thread
// ---------------------------------------------------------------------------

const CaseDetailDialog: React.FC<{
  caseId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onError: (msg: string) => void;
}> = ({ caseId, onClose, onChanged, onError }) => {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = useState<SupportCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const res = await supportApi.get(caseId);
      setDetail(res);
    } catch (e: any) {
      onError(e?.response?.data?.detail || t('common:feedback.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [caseId, onError, t]);

  useEffect(() => {
    if (caseId) {
      setReply('');
      load();
    } else {
      setDetail(null);
    }
  }, [caseId, load]);

  const handleSendReply = async () => {
    if (!caseId || !reply.trim()) return;
    setSubmitting(true);
    try {
      await supportApi.reply(caseId, reply.trim());
      setReply('');
      await load();
      onChanged();
    } catch (e: any) {
      onError(e?.response?.data?.detail || t('system:support.sendReplyFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseCase = async () => {
    if (!caseId) return;
    setClosing(true);
    try {
      await supportApi.close(caseId);
      setConfirmClose(false);
      await load();
      onChanged();
    } catch (e: any) {
      onError(e?.response?.data?.detail || t('system:support.closeCaseFailed'));
    } finally {
      setClosing(false);
    }
  };

  return (
    <Dialog open={!!caseId} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : detail ? (
        <>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{detail.subject}</Typography>
              <Stack direction="row" gap={0.75} sx={{ mt: 0.5 }}>
                <Chip label={supportStatusLabel(t, detail.status)} size="small" color={statusColor(detail.status)}
                  sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                <Chip label={supportPriorityLabel(t, detail.priority)} size="small" color={priorityColor(detail.priority)} variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }} />
                <Chip label={supportCategoryLabel(t, detail.category)} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Stack>
            </Box>
            <IconButton aria-label={t('common:actions.close')} size="small" onClick={onClose}><CloseOutlined fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Original message */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('system:support.originalMessage')} · {detail.created_at ? new Date(detail.created_at).toLocaleString(i18n.language) : ''}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                {detail.description}
              </Typography>
            </Box>

            <Divider />

            {/* Replies */}
            {(detail.replies?.length ?? 0) === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('system:support.noRepliesYet')}
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {(detail.replies ?? []).map((r: SupportCaseReply) => (
                  <Box
                    key={r.id}
                    sx={{
                      p: 1.5, borderRadius: 1,
                      bgcolor: r.author_role === 'admin' ? 'rgba(0,113,227,0.06)' : 'grey.100',
                      borderLeft: `3px solid ${r.author_role === 'admin' ? '#0071e3' : 'rgba(0,0,0,0.2)'}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                        {r.author_name ?? t('system:support.anonymous')}
                      </Typography>
                      <Chip
                        label={r.author_role === 'admin'
                          ? t('system:support.supportAuthor')
                          : t('system:support.youAuthor')}
                        size="small"
                        color={r.author_role === 'admin' ? 'primary' : 'default'}
                        sx={{ height: 16, fontSize: '0.625rem' }}
                      />
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 'auto' }}>
                        {r.created_at ? new Date(r.created_at).toLocaleString(i18n.language) : ''}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'pre-wrap' }}>
                      {r.message}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {/* Reply input — disabled if closed */}
            {detail.status === 'closed' ? (
              <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
                {t('system:support.closedNotice')}
              </Alert>
            ) : (
              <Box>
                <TextField
                  size="small" fullWidth multiline minRows={2} maxRows={6}
                  placeholder={t('system:support.replyPlaceholder')}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                  <Button
                    size="small" color="error" variant="text"
                    disabled={submitting || closing}
                    onClick={() => setConfirmClose(true)}
                    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8125rem' }}
                  >
                    {t('system:support.closeCase')}
                  </Button>
                  <Button
                    variant="contained" size="small"
                    disabled={!reply.trim() || submitting}
                    onClick={handleSendReply}
                    sx={{ borderRadius: 1, textTransform: 'none' }}
                  >
                    {submitting ? t('system:support.sending') : t('system:support.sendReply')}
                  </Button>
                </Stack>
              </Box>
            )}
          </DialogContent>
        </>
      ) : null}
      {/* Tenant-side close confirmation per ADR-019 Phase 3 — final action,
          a new case is required to follow up afterwards. */}
      <Dialog open={confirmClose} onClose={() => !closing && setConfirmClose(false)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>{t('system:support.closeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('system:support.closeConfirmBody')}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              size="small" onClick={() => setConfirmClose(false)} disabled={closing}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              size="small" color="error" variant="contained"
              disabled={closing}
              onClick={handleCloseCase}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              {closing ? t('system:support.closing') : t('system:support.closeCase')}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default SystemSupport;
